// SHOP SIGNS (DETAIL-1 D1-BLD) — the name board of every placed shop, built
// from the bake's anchor layer (buildings/shopsigns.json, the sign anchors of
// buildings/openings.json; pipeline/buildings.py plan_openings). The bake
// owns WHERE (a wall plane fit, the fascia of the shopfront ground floor or
// the storey line over the door, width from the name, ≤ 3 bays); this file
// only draws it: a board in the shop class's fascia colour 4 cm off the wall,
// its name from a TextAtlas slot (furniture.js, the trail signposts'
// program — no new shader), four edge strips back to the wall. ONE merged
// mesh, ONE draw, drawn ≤ near_m around the player and rebuilt after a
// step_m move (FurnGrid). Doors are baked into the building GLBs; signs are
// client-side so a later "real facade look" can repaint every atlas tile
// without moving one.
//
// shopsigns.json: {rev, count, signs: [{uuid, name, shop_kind, x, z, nx, nz,
//   ks, ky, y_ground, y, w, h, off, bg, fg}]} — (x, z) the wall point at
//   y_ground, n the outward normal, the wall surface off the anchor plane =
//   ks · s + ky · (y − y_ground) (s along R = (nz, −nx), right seen from
//   outside), the board centre at height y, w × h m, `off` m off the wall.

import * as THREE from 'three';
import { TextAtlas, atlasMaterial, addStyle, FurnGrid, needsRebuild, lin, shadeOf, FURN } from './furniture.js';

export const SIGNS = {
  near_m: 150,            // drawn ≤ this around the player
  step_m: 25,             // rebuild the drawn set after this move
  pad_m: 0.25,            // text inset each side (pipeline OPEN.sign_pad_m)
  font_px: 40,            // the slot's text px (pipeline OPEN.sign_font_px)
  char_f: 0.68,           // per-char width estimate (pipeline OPEN.sign_char_f)
  text_dz: 0.006,         // the text quad this far in front of the board
  edge_f: 0.62,           // the edge strips: board colour × this
  verts: 24, tris: 12,    // per sign: board, text, 4 edges
};

// the painted name's width in slot px (the pipeline's sign_width estimate)
export function textPx(name) {
  return Math.min(240, SIGNS.char_f * SIGNS.font_px * String(name || '').length);
}

// the text quad of a board w × h: [width, height, uv crop fraction of the slot]
export function textQuad(name, w, h) {
  const room = Math.max(0.05, w - 0.1);
  const need = (textPx(name) / 64) * h;          // the painted text at the board's height
  if (need <= room) {
    const tw = Math.min(room, 4 * h);
    return [tw, h, tw / (4 * h)];
  }
  const k = room / need;                          // a clamped board: the name shrinks to fit
  return [room, h * k, textPx(name) / FURN.slot_w];
}

const styleOf = (bg, fg) => {
  const k = `shop${bg}${fg}`;
  addStyle(k, { bg, fg, px: SIGNS.font_px, bold: true });
  return k;
};

// one sign's vertices: P (xyz), C (rgb), U (uv), 24 each → written at v0
export function signVerts(s, atlas, slot, H, out, v0) {
  const nx = s.nx, nz = s.nz, rx = nz, rz = -nx;
  const ks = s.ks || 0, ky = s.ky || 0, yg = s.y_ground, off = s.off ?? 0.06;
  const sh = shadeOf(nx, 0, nz);
  const bg = lin(s.bg).map((c) => c * sh), ed = bg.map((c) => c * SIGNS.edge_f), wt = [sh, sh, sh];
  const W0 = atlas.rect(0, H), wu = (W0[0] + W0[2]) / 2, wv = (W0[1] + W0[3]) / 2;
  let v = v0;
  const put = (sx, yy, d, col, u, vv) => {
    const o = d + ks * sx + ky * (yy - yg);
    out.pos[v * 3] = s.x + rx * sx + nx * o; out.pos[v * 3 + 1] = yy; out.pos[v * 3 + 2] = s.z + rz * sx + nz * o;
    out.col[v * 3] = col[0]; out.col[v * 3 + 1] = col[1]; out.col[v * 3 + 2] = col[2];
    out.uv[v * 2] = u; out.uv[v * 2 + 1] = vv;
    v++;
  };
  const l = -s.w / 2, r = s.w / 2, b = s.y - s.h / 2, t = s.y + s.h / 2;
  // board (white slot × the fascia colour), wound to face outward
  put(l, b, off, bg, wu, wv); put(r, b, off, bg, wu, wv); put(r, t, off, bg, wu, wv); put(l, t, off, bg, wu, wv);
  // the name
  const [tw, th, f] = textQuad(s.name, s.w, s.h);
  const R = atlas.rect(slot, H), uc = (R[0] + R[2]) / 2, hu = ((R[2] - R[0]) * Math.min(1, f)) / 2;
  const vc = (R[1] + R[3]) / 2, hv = (R[3] - R[1]) / 2;
  const d2 = off + SIGNS.text_dz;
  put(-tw / 2, s.y - th / 2, d2, wt, uc - hu, vc - hv); put(tw / 2, s.y - th / 2, d2, wt, uc + hu, vc - hv);
  put(tw / 2, s.y + th / 2, d2, wt, uc + hu, vc + hv); put(-tw / 2, s.y + th / 2, d2, wt, uc - hu, vc + hv);
  // the four edges, board → wall
  for (const [a0, a1] of [[[l, b], [r, b]], [[r, b], [r, t]], [[r, t], [l, t]], [[l, t], [l, b]]]) {
    put(a0[0], a0[1], 0, ed, wu, wv); put(a1[0], a1[1], 0, ed, wu, wv);
    put(a1[0], a1[1], off, ed, wu, wv); put(a0[0], a0[1], off, ed, wu, wv);
  }
  return v;
}

export function signIndex(idx, i0, v0) {
  let i = i0;
  for (let q = 0; q < 6; q++) {
    const a = v0 + q * 4;
    idx[i++] = a; idx[i++] = a + 1; idx[i++] = a + 2;
    idx[i++] = a; idx[i++] = a + 2; idx[i++] = a + 3;
  }
  return i;
}

// signs → {list (valid), atlas, slots, stats}: deterministic (file order)
export function planSigns(doc) {
  const list = (doc && Array.isArray(doc.signs) ? doc.signs : []).filter((s) => s && s.name
    && [s.x, s.z, s.nx, s.nz, s.y, s.y_ground, s.w, s.h].every(Number.isFinite) && s.w > 0 && s.h > 0);
  const keys = new Set(list.map((s) => `${s.bg}${s.fg}\u0001${s.name}`));
  const atlas = new TextAtlas(Math.max(1, Math.ceil((keys.size + 1) / 64)));
  const slots = list.map((s) => atlas.slot(styleOf(s.bg || '#34373a', s.fg || '#f5f5f5'), String(s.name)));
  return { list, atlas, slots, stats: { signs: list.length, texts: atlas.keys.size, refused: atlas.refused,
    tris_all: list.length * SIGNS.tris } };
}

export class ShopSigns {
  constructor(scene) {
    this.scene = scene;
    this.mesh = null; this.plan = null; this.grid = null; this._last = null; this.stats = null; this.drawn = null;
  }

  build(doc) {
    this.dispose();
    const P = planSigns(doc);
    this.plan = P;
    this.stats = { ...P.stats, draws: 0 };
    const n = P.list.length;
    if (!n) return this.stats;
    this.H = P.atlas.height();
    // every sign's vertices once (≈ 200 signs × 24 verts): a rebuild copies slices
    this.all = { pos: new Float32Array(n * SIGNS.verts * 3), col: new Float32Array(n * SIGNS.verts * 3),
      uv: new Float32Array(n * SIGNS.verts * 2) };
    P.list.forEach((s, i) => signVerts(s, P.atlas, P.slots[i], this.H, this.all, i * SIGNS.verts));
    const g = { n, x: new Float32Array(n), z: new Float32Array(n), g: new Uint8Array(n) };
    P.list.forEach((s, i) => { g.x[i] = s.x; g.z[i] = s.z; });
    this.grid = new FurnGrid(g);
    this.vis = new Int32Array(n);
    const tex = typeof document !== 'undefined' ? P.atlas.paint(this.H) : null;
    const geo = new THREE.BufferGeometry();
    const A = (k, m) => { const a = new THREE.BufferAttribute(new Float32Array(n * SIGNS.verts * m), m); a.setUsage(THREE.DynamicDrawUsage); return a; };
    geo.setAttribute('position', A('position', 3));
    geo.setAttribute('color', A('color', 3));
    geo.setAttribute('uv', A('uv', 2));
    const ix = new THREE.BufferAttribute(new Uint32Array(n * SIGNS.tris * 3), 1);
    ix.setUsage(THREE.DynamicDrawUsage);
    geo.setIndex(ix);
    geo.setDrawRange(0, 0);
    this.mesh = new THREE.Mesh(geo, atlasMaterial(tex));
    this.mesh.name = 'shop-signs';
    this.mesh.frustumCulled = false;     // a ≤ 150 m ring around the player
    this.mesh.matrixAutoUpdate = false;
    this.mesh.updateMatrixWorld(true);
    if (this.scene) this.scene.add(this.mesh);
    Object.assign(this.stats, { draws: 1, atlas_texels: `${FURN.atlas_w}x${this.H}` });
    return this.stats;
  }

  dispose() {
    if (this.mesh) {
      if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
      this.mesh.geometry.dispose();
      if (this.mesh.material.map) this.mesh.material.map.dispose();
      this.mesh.material.dispose();
    }
    this.mesh = null; this.grid = null; this._last = null;
  }

  // per frame: the drawn set after a step_m move (or force)
  update(pos, force = false) {
    if (!this.mesh || !pos) return false;
    if (!force && !needsRebuild(this._last, pos.x, pos.y, pos.z, SIGNS.step_m)) return false;
    this._last = { x: pos.x, y: pos.y, z: pos.z };
    const k = this.grid.visible(pos.x, pos.z, this.vis, SIGNS.near_m, SIGNS.near_m);
    const geo = this.mesh.geometry, a = geo.attributes, idx = geo.index.array;
    const V = SIGNS.verts;
    let ii = 0;
    for (let j = 0; j < k; j++) {
      const i = this.vis[j];
      a.position.array.set(this.all.pos.subarray(i * V * 3, (i + 1) * V * 3), j * V * 3);
      a.color.array.set(this.all.col.subarray(i * V * 3, (i + 1) * V * 3), j * V * 3);
      a.uv.array.set(this.all.uv.subarray(i * V * 2, (i + 1) * V * 2), j * V * 2);
      ii = signIndex(idx, ii, j * V);
    }
    for (const [att, m] of [[a.position, 3], [a.color, 3], [a.uv, 2]]) {
      att.clearUpdateRanges(); att.addUpdateRange(0, k * V * m); att.needsUpdate = true;
    }
    geo.index.clearUpdateRanges(); geo.index.addUpdateRange(0, ii); geo.index.needsUpdate = true;
    geo.setDrawRange(0, ii);
    this.drawn = { signs: k, tris: ii / 3 };
    return true;
  }
}
